import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import { authService } from "../../api/authService";

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f8fa;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 1.2rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.09);
  padding: 2.5rem 2.2rem 2.2rem 2.2rem;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #e97d7dff;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Label = styled.label`
  font-size: 1rem;
  color: #444;
  margin-bottom: 0.3rem;
  display: block;
`;

const Input = styled.input<{ error?: boolean }>`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: 0.7rem;
  border: 1.5px solid ${(p) => (p.error ? "#e97d7dff" : "#e0e0e0")};
  font-size: 1rem;
  margin-bottom: 0.7rem;
  background: #fafafa;
  transition: border 0.2s;
  &:focus {
    border-color: #e97d7dff;
    outline: none;
  }
`;

const ErrorMsg = styled.div`
  color: #e97d7dff;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
`;

const Button = styled.button`
  background: #e97d7dff;
  color: #fff;
  border: none;
  border-radius: 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2.2rem;
  cursor: pointer;
  margin-top: 0.7rem;
  margin-bottom: 0.7rem;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #fff;
    color: #e97d7dff;
    border: 2px solid #e97d7dff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CheckboxLabel = styled.label`
  font-size: 0.98rem;
  color: #444;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const BottomText = styled.div`
  text-align: center;
  font-size: 1rem;
  margin-top: 1.2rem;
  color: #444;
`;

const Link = styled.a`
  color: #e97d7dff;
  font-size: 0.98rem;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #000;
  }
`;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRegex = /^(?=.*\d).{8,}$/;

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm: "",
    terms: false,
  });
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    const prefillEmail = localStorage.getItem("prefillEmail");
    if (prefillEmail) {
      setForm((prev) => ({ ...prev, email: prefillEmail }));
      localStorage.removeItem("prefillEmail");
    }
  }, []);

  const validate = () => {
    const errs: { [k: string]: string } = {};
    if (!form.name) errs.name = "Full name required.";
    if (!emailRegex.test(form.email)) errs.email = "Invalid email format.";
    if (!passwordRegex.test(form.password))
      errs.password =
        "Password must be at least 8 characters and include a number.";
    if (form.confirm !== form.password)
      errs.confirm = "Passwords do not match.";
    if (!form.terms) errs.terms = "You must accept the terms.";
    return errs;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    setErrors((errs) => {
      const newErrs = { ...errs };
      delete newErrs[name];
      return newErrs;
    });
    setApiError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    setApiError("");
    try {
      await signUp(form.email, form.password);
      // Send welcome email
      try {
        await authService.sendWelcomeEmail({
          customerName: form.name,
          customerEmail: form.email,
        });
      } catch (emailError) {
        console.error("Failed to send welcome email:", emailError);
      }

      navigate("/");
    } catch (err: any) {
      if (err.code === "auth/email-already-in-use") {
        setApiError("Email already exists.");
      } else {
        setApiError("Failed to sign up.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Card
        as="form"
        onSubmit={handleSubmit}
        autoComplete="off"
        aria-label="Sign Up Form"
      >
        <Title>Create Your Account</Title>
        <Label htmlFor="name">Full Name</Label>
        <Input
          id="name"
          name="name"
          value={form.name}
          onChange={handleChange}
          error={!!errors.name}
          required
          aria-invalid={!!errors.name}
        />
        {errors.name && <ErrorMsg>{errors.name}</ErrorMsg>}
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          error={!!errors.email}
          autoComplete="email"
          required
          aria-invalid={!!errors.email}
        />
        {errors.email && <ErrorMsg>{errors.email}</ErrorMsg>}
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          name="password"
          type="password"
          value={form.password}
          onChange={handleChange}
          error={!!errors.password}
          autoComplete="new-password"
          required
          aria-invalid={!!errors.password}
        />
        {errors.password && <ErrorMsg>{errors.password}</ErrorMsg>}
        <Label htmlFor="confirm">Confirm Password</Label>
        <Input
          id="confirm"
          name="confirm"
          type="password"
          value={form.confirm}
          onChange={handleChange}
          error={!!errors.confirm}
          autoComplete="new-password"
          required
          aria-invalid={!!errors.confirm}
        />
        {errors.confirm && <ErrorMsg>{errors.confirm}</ErrorMsg>}
        <CheckboxLabel>
          <input
            type="checkbox"
            name="terms"
            checked={form.terms}
            onChange={handleChange}
          />
          I accept the{" "}
          <Link onClick={() => navigate("/terms-and-conditions")}>
            Terms & Conditions
          </Link>
        </CheckboxLabel>
        {errors.terms && <ErrorMsg>{errors.terms}</ErrorMsg>}
        <Button
          type="submit"
          disabled={loading || Object.keys(validate()).length > 0}
          aria-disabled={loading || Object.keys(validate()).length > 0}
        >
          {loading ? "Creating Account..." : "Sign Up"}
        </Button>
        {apiError && <ErrorMsg>{apiError}</ErrorMsg>}
        <BottomText>
          Already have an account?{" "}
          <Link as="button" type="button" onClick={() => navigate("/signin")}>
            Sign In
          </Link>
        </BottomText>
      </Card>
    </Wrapper>
  );
};

export default SignUp;
